<template>
  <div :class="['filter-chips-item', nonRemovable ? 'non-removable' : '']">
    <sl-tooltip v-if="showTooltip(item)" :style="tooltipMaxWidthPx" hoist :placement="tooltipPlacement">
      <div slot="content">{{ item.tooltipText }}</div>
      <span class="label">{{ item.label }}</span>
    </sl-tooltip>
    <span v-else class="label">{{ item.label }}</span>
    <span v-if="!nonRemovable" class="icon-close-cross-exit-quit-delete" :class="[checkUnsavedChanges ? SHOW_UNSAVED_CHANGES_CSS_CLASS_NAME : '', disabled ? 'disabled': '']" @click="handleRemoveButtonClick"></span>
  </div>
</template>

<script lang="ts" setup>
import { computed, ref, toRefs } from 'vue';
import "@shoelace-style/shoelace/dist/components/tooltip/tooltip.js";
import { SHOW_UNSAVED_CHANGES_CSS_CLASS_NAME } from "@/constants/cssClassNames";
import { IFilterChips, IFilterChipsEventData } from '@/models/filter.model';

interface IFilterChipsItemProps {
  disabled?: boolean;
  item: IFilterChips;
  labelFontSize?: number;
  labelMaxWidth?: number;
  nonRemovable?: boolean;
  tooltipMaxWidth?: number;
  tooltipPlacement?: string;
  checkUnsavedChanges?: boolean;
}

const props = withDefaults(defineProps<IFilterChipsItemProps>(), {
  disabled: false,
  labelFontSize: 12,
  labelMaxWidth: 270,
  nonRemovable: false,
  tooltipMaxWidth: 280,
  tooltipPlacement: 'top',
  checkUnsavedChanges: true,
});

const {
  disabled,
  item,
  labelFontSize,
  labelMaxWidth,
  nonRemovable,
  tooltipMaxWidth,
  tooltipPlacement,
  checkUnsavedChanges,
} = toRefs(props);

const tooltipMaxWidthPx = computed(() => `--max-width: ${tooltipMaxWidth.value}px;`);
const labelMaxWidthPx = ref(`${labelMaxWidth.value}px`);
const labelFontSizePx = ref(`${labelFontSize.value}px`);

const emits = defineEmits(["remove"]);

const showTooltip = (item: IFilterChips): boolean => {
  if (!item || !item.label) return false;
  
  if (item.showTooltip) {
    return true;
  }

  const span = document.createElement('span');
  span.style.visibility = 'hidden';
  span.style.position = 'absolute';
  span.style.whiteSpace = 'nowrap';
  span.style.fontSize = labelFontSizePx.value;
  span.textContent = item.label;
  document.body.appendChild(span);
  const fits = span.offsetWidth <= labelMaxWidth.value;
  document.body.removeChild(span);

  return !fits;
}

const handleRemoveButtonClick = () => {
  if (disabled.value || nonRemovable.value) return;

  const eventData = {
    itemId: item.value.id,
    property: item.value.property,
  } as IFilterChipsEventData;

  emits("remove", eventData);
};
</script>

<style>
:root {
  --filter-chips-background-color: var(--colour-background-brand-default);
  --filter-chips-font-color: var(--colour-text-brand-default);
  --filter-chips-icon-color: var(--colour-icons-brand-subtle);
  --filter-chips-icon-disabled-color: var(--colour-icons-disabled);
}
</style>

<style lang="scss" scoped>
.filter-chips-item {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 4px;
  height: 24px;
  padding: 4px 2px 4px 8px;
  border-radius: 24px;
  background-color: var(--filter-chips-background-color);

  .label {
    color: var(--filter-chips-font-color);
    font-size: v-bind(labelFontSizePx);
    font-weight: var(--font-weight-body-regular);
    line-height: 12px;
    max-width: v-bind(labelMaxWidthPx);
    overflow: hidden;
    text-wrap-mode: nowrap;
    text-overflow: ellipsis;
    user-select: none;
  }

  .icon-close-cross-exit-quit-delete {
    display: flex;
    font-size: 22px;
    width: 22px;
    color: var(--filter-chips-icon-color);
    cursor: pointer;
  }

  .icon-close-cross-exit-quit-delete.disabled {
    color: var(--colour-icons-disabled);
    cursor: not-allowed;
  }

  sl-tooltip {
    &::part(body) {
      word-wrap: break-word;
      text-wrap-style: balance;
    }
  }
}

.filter-chips-item.non-removable {
  padding-right: 8px;
}
</style>